import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    Stack,
} from "@mui/material";
import ProjectPicker from "../ProjectPicker";
import ServicePicker from "../ServicePicker";
import HSEPicker from "../HSEPicker";
import QuotePicker from "../QuotePicker";
import ProjectTypeDropdown from "../ProjectTypeDropdown";
import SimpleFloorPlan from "./SimpleFloorPlan";
import TestPointSelector from "./TestPointSelector";
import FrequencyReferenceEntry from "./FrequencyReferenceEntry";
import ScoreEntry from "./ScoreEntry";
import MagnetCarousel from "./MagnetCarousel";
import ResetConfirmDialog from "./ResetConfirmDialog";
import DeleteReportDialog from "./DeleteReportDialog";
import NotesEntry from "./NotesEntry";
import ReportDataService from "../../services/Report.services";

export default function RFTestReport(props) {
    const { user, token, handleOpenSnackbar, darkState } = props;

    // Load from localStorage on mount
    const loadFromStorage = () => {
        try {
            const saved = localStorage.getItem("rfTestReportData");
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error("Error loading RF Test Report data:", e);
        }
        return null;
    };

    const savedData = loadFromStorage();

    const [mode, setMode] = useState((savedData && savedData.mode) || null); // null, "create", or "load"
    const [isLoadedReport, setIsLoadedReport] = useState(
        (savedData && savedData.isLoadedReport) || false,
    );
    const [activeStep, setActiveStep] = useState(
        (savedData && savedData.activeStep) || -1,
    );
    const [project, setProject] = useState(
        (savedData && savedData.project) || null,
    );
    const [service, setService] = useState(
        (savedData && savedData.service) || null,
    );
    const [hse, setHse] = useState((savedData && savedData.hse) || null);
    const [quote, setQuote] = useState((savedData && savedData.quote) || null);
    const [menuSelection, setMenuSelection] = useState(
        (savedData && savedData.menuSelection) || 0,
    );
    const [reportType, setReportType] = useState(
        (savedData && savedData.reportType) || "",
    );
    const [magnetType, setMagnetType] = useState(
        (savedData && savedData.magnetType) || "",
    );
    const [floorPlanData, setFloorPlanData] = useState(
        (savedData && savedData.floorPlanData) || null,
    );
    const [testPoints, setTestPoints] = useState(
        (savedData && savedData.testPoints) || [],
    );
    const [frequencies, setFrequencies] = useState(
        (savedData && savedData.frequencies) || [],
    );
    const [scores, setScores] = useState((savedData && savedData.scores) || {});
    const [scoresComplete, setScoresComplete] = useState(false);
    const [witness, setWitness] = useState(
        savedData && savedData.witness !== undefined ? savedData.witness : true,
    );
    const [witnessName, setWitnessName] = useState(
        (savedData && savedData.witnessName) || "",
    );
    const [witnessCompany, setWitnessCompany] = useState(
        (savedData && savedData.witnessCompany) || "",
    );
    const [notes, setNotes] = useState((savedData && savedData.notes) || "");
    const [createdBy, setCreatedBy] = useState(
        (savedData && savedData.createdBy) || null,
    );
    const [createdDate, setCreatedDate] = useState(
        (savedData && savedData.createdDate) || null,
    );
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectionError, setSelectionError] = useState("");
    const [loadedReportId, setLoadedReportId] = useState(null);
    const [availableReports, setAvailableReports] = useState([]);
    const [selectedReportFromList, setSelectedReportFromList] = useState(null);
    const [loadingReports, setLoadingReports] = useState(false);

    // Load a specific report into state
    const loadSpecificReport = (report) => {
        setLoadedReportId(report.id);
        setReportType(
            report.test_type === "Baseline"
                ? "B"
                : report.test_type === "Preliminary"
                ? "P"
                : report.test_type === "Final"
                ? "F"
                : "",
        );
        setMagnetType(report.magnet_type || "");

        // Parse floor_plan if it's a string
        let parsedFloorPlan = report.floor_plan;
        if (typeof parsedFloorPlan === "string" && parsedFloorPlan) {
            try {
                parsedFloorPlan = JSON.parse(parsedFloorPlan);
            } catch (e) {
                console.error("Error parsing floor_plan:", e);
                parsedFloorPlan = null;
            }
        }
        setFloorPlanData(parsedFloorPlan || null);

        setTestPoints(report.test_points || []);
        setFrequencies(report.frequencies || []);
        setScores(report.scores || {});
        setWitnessName(report.witness_name || "");
        setWitnessCompany(report.witness_company || "");
        setNotes(report.notes || "");
        setWitness(!!(report.witness_name || report.witness_company));
        setCreatedBy(report.created_by || null);
        setCreatedDate(report.created_date || null);
        setIsLoadedReport(true);
    };

    // Load report from API
    const loadReportFromAPI = async (item, itemType) => {
        setLoadingReports(true);
        try {
            const response = await ReportDataService.getRFTestReports(token);
            const allReports = response.data;

            // Filter reports based on the selected item type
            let matchingReports = [];
            if (itemType === "project" && item) {
                matchingReports = allReports.filter(
                    (r) => r.project && r.project.id === item.id,
                );
            } else if (itemType === "service" && item) {
                matchingReports = allReports.filter(
                    (r) => r.service && r.service.id === item.id,
                );
            } else if (itemType === "hse" && item) {
                matchingReports = allReports.filter(
                    (r) => r.hse && r.hse.id === item.id,
                );
            } else if (itemType === "quote" && item) {
                matchingReports = allReports.filter(
                    (r) => r.quote && r.quote.id === item.id,
                );
            }

            if (matchingReports.length > 0) {
                setAvailableReports(matchingReports);

                if (matchingReports.length === 1) {
                    // Only one report found, load it automatically
                    loadSpecificReport(matchingReports[0]);
                    setSelectedReportFromList(matchingReports[0]);
                    if (handleOpenSnackbar) {
                        handleOpenSnackbar(
                            "success",
                            "Report loaded successfully!",
                        );
                    }
                } else {
                    // Multiple reports found, let user choose
                    setLoadedReportId(null);
                    setSelectedReportFromList(null);
                    // Clear all report fields to prevent flash of data
                    setReportType("");
                    setMagnetType("");
                    setFloorPlanData(null);
                    setTestPoints([]);
                    setFrequencies([]);
                    setScores({});
                    setWitnessName("");
                    setWitnessCompany("");
                    setNotes("");
                    setWitness(true);
                    if (handleOpenSnackbar) {
                        handleOpenSnackbar(
                            "info",
                            `Found ${matchingReports.length} reports. Please select one.`,
                        );
                    }
                }
            } else {
                // No existing report found for this item
                setAvailableReports([]);
                setLoadedReportId(null);
                setSelectedReportFromList(null);
                // Clear all report fields
                setReportType("");
                setMagnetType("");
                setFloorPlanData(null);
                setTestPoints([]);
                setFrequencies([]);
                setScores({});
                setWitnessName("");
                setWitnessCompany("");
                setNotes("");
                setWitness(true);
                if (handleOpenSnackbar) {
                    handleOpenSnackbar(
                        "info",
                        "No existing report found for this item.",
                    );
                }
            }
        } catch (error) {
            console.error("Error loading report from API:", error);
            if (handleOpenSnackbar) {
                handleOpenSnackbar("error", "Failed to load report from API");
            }
        } finally {
            setLoadingReports(false);
        }
    };

    // Clear selections when menu type changes
    useEffect(() => {
        if (activeStep >= 0) {
            setProject(null);
            setService(null);
            setHse(null);
            setQuote(null);
        }
    }, [menuSelection]);

    // Auto-save to localStorage whenever data changes
    useEffect(() => {
        const dataToSave = {
            mode,
            isLoadedReport,
            activeStep,
            project,
            service,
            hse,
            quote,
            menuSelection,
            reportType,
            magnetType,
            floorPlanData,
            testPoints,
            frequencies,
            scores,
            witness,
            witnessName,
            witnessCompany,
            notes,
            createdBy,
            createdDate,
            lastSaved: new Date().toISOString(),
        };
        localStorage.setItem("rfTestReportData", JSON.stringify(dataToSave));
    }, [
        mode,
        isLoadedReport,
        activeStep,
        project,
        service,
        hse,
        quote,
        menuSelection,
        reportType,
        magnetType,
        floorPlanData,
        testPoints,
        frequencies,
        scores,
        witness,
        witnessName,
        witnessCompany,
        notes,
        createdBy,
        createdDate,
    ]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setMode(null);
        setIsLoadedReport(false);
        setActiveStep(-1);
        setProject(null);
        setService(null);
        setHse(null);
        setQuote(null);
        setMenuSelection(0);
        setReportType("");
        setMagnetType("");
        setFloorPlanData(null);
        setTestPoints([]);
        setFrequencies([]);
        setScores({});
        setWitness(true);
        setWitnessName("");
        setWitnessCompany("");
        setNotes("");
        setCreatedBy(null);
        setCreatedDate(null);
        setLoadedReportId(null);
        setAvailableReports([]);
        setSelectedReportFromList(null);
        localStorage.removeItem("rfTestReportData");
        localStorage.removeItem("floorPlanShapes");
    };

    const handleSelectMode = (selectedMode) => {
        setMode(selectedMode);
        setActiveStep(0);
        if (selectedMode === "load") {
            setIsLoadedReport(true);
        }
    };

    // Validation helper: Ensure exactly one selection
    const validateSelection = (projectVal, serviceVal, hseVal, quoteVal) => {
        const selections = [projectVal, serviceVal, hseVal, quoteVal].filter(
            (val) => val !== null,
        );
        if (selections.length === 0) {
            return "Please select a Project, Service, HSE, or Quote";
        }
        if (selections.length > 1) {
            return "Only one of Project, Service, HSE, or Quote can be selected";
        }
        return "";
    };

    const handleChangeProject = (newValue) => {
        setProject(newValue);
        setService(null);
        setHse(null);
        setQuote(null);
        setSelectionError(validateSelection(newValue, null, null, null));
        setAvailableReports([]);
        setSelectedReportFromList(null);
        setLoadingReports(false);

        // Clear report fields to prevent flash
        if (mode === "load") {
            setReportType("");
            setMagnetType("");
            setFloorPlanData(null);
            setTestPoints([]);
            setFrequencies([]);
            setScores({});
            setWitnessName("");
            setWitnessCompany("");
            setNotes("");
            setWitness(true);
        }

        // If in load mode and project is selected, fetch existing report
        if (mode === "load" && newValue) {
            loadReportFromAPI(newValue, "project");
        }
    };

    const handleChangeService = (newValue) => {
        setService(newValue);
        setProject(null);
        setHse(null);
        setQuote(null);
        setSelectionError(validateSelection(null, newValue, null, null));
        setAvailableReports([]);
        setSelectedReportFromList(null);
        setLoadingReports(false);

        // Clear report fields to prevent flash
        if (mode === "load") {
            setReportType("");
            setMagnetType("");
            setFloorPlanData(null);
            setTestPoints([]);
            setFrequencies([]);
            setScores({});
            setWitnessName("");
            setWitnessCompany("");
            setNotes("");
            setWitness(true);
        }

        // If in load mode and service is selected, fetch existing report
        if (mode === "load" && newValue) {
            loadReportFromAPI(newValue, "service");
        }
    };

    const handleChangeHse = (newValue) => {
        setHse(newValue);
        setProject(null);
        setService(null);
        setQuote(null);
        setSelectionError(validateSelection(null, null, newValue, null));
        setAvailableReports([]);
        setSelectedReportFromList(null);
        setLoadingReports(false);

        // Clear report fields to prevent flash
        if (mode === "load") {
            setReportType("");
            setMagnetType("");
            setFloorPlanData(null);
            setTestPoints([]);
            setFrequencies([]);
            setScores({});
            setWitnessName("");
            setWitnessCompany("");
            setNotes("");
            setWitness(true);
        }

        // If in load mode and HSE is selected, fetch existing report
        if (mode === "load" && newValue) {
            loadReportFromAPI(newValue, "hse");
        }
    };

    const handleChangeQuote = (newValue) => {
        setQuote(newValue);
        setProject(null);
        setService(null);
        setHse(null);
        setSelectionError(validateSelection(null, null, null, newValue));
        setAvailableReports([]);
        setSelectedReportFromList(null);
        setLoadingReports(false);

        // Clear report fields to prevent flash
        if (mode === "load") {
            setReportType("");
            setMagnetType("");
            setFloorPlanData(null);
            setTestPoints([]);
            setFrequencies([]);
            setScores({});
            setWitnessName("");
            setWitnessCompany("");
            setNotes("");
            setWitness(true);
        }

        // If in load mode and quote is selected, fetch existing report
        if (mode === "load" && newValue) {
            loadReportFromAPI(newValue, "quote");
        }
    };

    const handleChangeMagnetType = (value) => {
        setMagnetType(value);
    };

    const handleSaveFloorPlan = (data) => {
        setFloorPlanData(data);
        handleNext();
    };

    const handleSaveTestPoints = (points) => {
        setTestPoints(points);
        handleNext();
    };

    const handleSaveFrequencies = (freqs) => {
        setFrequencies(freqs);
        handleNext();
    };

    const downloadJSON = (data, filename) => {
        const jsonStr = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonStr], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleLoadJSON = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const jsonData = JSON.parse(e.target.result);

                // Load the data into state (supporting both old and new formats)
                setProject(
                    jsonData.project || jsonData.project_details || null,
                );
                setService(
                    jsonData.service || jsonData.service_details || null,
                );
                setHse(jsonData.hse || jsonData.hse_details || null);
                setQuote(jsonData.quote || jsonData.quote_details || null);
                setReportType(
                    jsonData.test_type === "Baseline"
                        ? "B"
                        : jsonData.test_type === "Preliminary"
                        ? "P"
                        : jsonData.test_type === "Final"
                        ? "F"
                        : "",
                );
                setMagnetType(jsonData.magnet_type || "");
                setFloorPlanData(
                    jsonData.floor_plan || jsonData.floorPlanData || null,
                );
                // Load test points as-is (with polarization) so they match score keys
                setTestPoints(
                    jsonData.test_points || jsonData.testPoints || [],
                );
                setFrequencies(jsonData.frequencies || []);
                setScores(jsonData.scores || {});
                setWitnessName(jsonData.witness_name || "");
                setWitnessCompany(jsonData.witness_company || "");
                setNotes(jsonData.notes || "");
                setWitness(
                    !!(jsonData.witness_name || jsonData.witness_company),
                );
                // Preserve original creator info when loading existing report
                setCreatedBy(jsonData.created_by || null);
                setCreatedDate(jsonData.created_date || null);

                // Set mode and advance to step 0
                setMode("load");
                setIsLoadedReport(true);
                setActiveStep(0);

                if (handleOpenSnackbar) {
                    handleOpenSnackbar(
                        "success",
                        "JSON file loaded successfully!",
                    );
                }
            } catch (error) {
                console.error("Error parsing JSON:", error);
                if (handleOpenSnackbar) {
                    handleOpenSnackbar("error", "Failed to load JSON file");
                }
            }
        };
        reader.readAsText(file);

        // Reset the input so the same file can be loaded again
        event.target.value = "";
    };

    const handleSaveScores = (scoreData) => {
        setScores(scoreData);
        handleNext();
    };

    const handleDeleteReport = async () => {
        if (!loadedReportId) {
            if (handleOpenSnackbar) {
                handleOpenSnackbar("error", "No report loaded to delete");
            }
            return;
        }

        try {
            await ReportDataService.deleteRFTestReport(loadedReportId, token);
            if (handleOpenSnackbar) {
                handleOpenSnackbar("success", "Report deleted successfully!");
            }
            handleReset();
        } catch (error) {
            console.error("Error deleting report:", error);
            if (handleOpenSnackbar) {
                handleOpenSnackbar("error", "Failed to delete report");
            }
        }
    };

    const handleSaveReport = async () => {
        // Validate exactly one selection
        const selections = [project, service, hse, quote].filter(
            (val) => val !== null,
        );
        if (selections.length !== 1) {
            if (handleOpenSnackbar) {
                handleOpenSnackbar(
                    "error",
                    "Exactly one of Project, Service, HSE, or Quote must be selected",
                );
            }
            return;
        }

        // Determine which item is selected and get its number for report number
        const selectedItem = project || service || hse || quote;
        const reportNumber = selectedItem
            ? `${selectedItem.number}-${reportType}`
            : null;

        // Get full report type name
        const getReportTypeName = (type) => {
            const typeMap = {
                B: "Baseline",
                P: "Preliminary",
                F: "Final",
            };
            return typeMap[type] || type;
        };

        // Extract magnet strength for GE magnets only
        const extractMagnetStrength = (magnetTypeValue) => {
            console.log("extractMagnetStrength called with:", magnetTypeValue);

            if (!magnetTypeValue) {
                console.log("magnetType is falsy, returning null");
                return null;
            }

            if (magnetTypeValue.startsWith("GE")) {
                // Extract "1.5T" or "3.0T" from "GE 1.5T" or "GE 3.0T"
                const match = magnetTypeValue.match(/(\d+\.\d+T)/);
                const strength = match ? match[1] : null;
                console.log(
                    "GE magnet detected, extracted strength:",
                    strength,
                );
                return strength;
            }

            console.log("Non-GE magnet, returning null");
            return null; // All non-GE magnets have null strength
        };

        // Prepare data for API format - structured for Word document import and database
        const reportData = {
            // Nested relational objects (simulating API structure)
            project: project ? project.id : null,
            service: service ? service.id : null,
            hse: hse ? hse.id : null,
            quote: quote ? quote.id : null,

            // Word document template fields
            report_number: reportNumber || "",
            test_type: getReportTypeName(reportType),
            magnet_type: magnetType || "",
            magnet_strength: extractMagnetStrength(magnetType),
            floor_plan: floorPlanData ? JSON.stringify(floorPlanData) : null,
            witness_name: witness ? witnessName : "",
            witness_company: witness ? witnessCompany : "",
            scores: scores || {},
            notes: notes || "",

            // Additional data for database
            test_points: testPoints || [],
            frequencies: frequencies || [],
        };

        // Format date as MMDDYY
        const formatDateMMDDYY = (date) => {
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const year = String(date.getFullYear()).slice(-2);
            return `${month}${day}${year}`;
        };

        // Download as JSON file
        const currentDate = new Date();
        const dateStr = formatDateMMDDYY(currentDate);
        const reportTypeName = getReportTypeName(reportType);
        const filename = reportNumber
            ? `${reportNumber} ${reportTypeName} RF Test ${dateStr}.json`
            : `RF_Test_Report_${dateStr}.json`;

        // Save to API (create or update)
        try {
            if (loadedReportId) {
                // Update existing report
                await ReportDataService.updateRFTestReport(
                    loadedReportId,
                    reportData,
                    token,
                );
                if (handleOpenSnackbar) {
                    handleOpenSnackbar(
                        "success",
                        "RF Test Report updated successfully!",
                    );
                }
            } else {
                // Create new report
                await ReportDataService.createRFTestReport(reportData, token);
                if (handleOpenSnackbar) {
                    handleOpenSnackbar(
                        "success",
                        "RF Test Report created successfully!",
                    );
                }
            }

            // Also download as JSON file for backup
            // downloadJSON(reportData, filename);

            // Clear the draft after successful save
            localStorage.removeItem("rfTestReportData");

            // Move to completion
            handleNext();
        } catch (error) {
            console.error("Error saving report to API:", error);
            if (handleOpenSnackbar) {
                handleOpenSnackbar("error", "Failed to save report to API");
            }
        }
        // console.log("Prepared report data for saving:", reportData);
    };

    const getStepContent = (step) => {
        switch (step) {
            case -1:
                return (
                    <Box
                        sx={{
                            py: { xs: 4, sm: 6 },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 3,
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Choose an option to get started:
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                gap: 3,
                                width: "100%",
                                maxWidth: 600,
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => handleSelectMode("create")}
                                sx={{
                                    flex: 1,
                                    py: 3,
                                    mx: 3,
                                }}
                            >
                                Create New Report
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => handleSelectMode("load")}
                                sx={{
                                    flex: 1,
                                    py: 3,
                                    mx: 3,
                                }}
                            >
                                Load Existing Report
                            </Button>
                        </Box>
                        {/* Temporary testing button - COMMENTED OUT */}
                        {/* <Box sx={{ mt: 4, textAlign: "center" }}>
                            <input
                                accept=".json"
                                style={{ display: "none" }}
                                id="load-json-file"
                                type="file"
                                onChange={handleLoadJSON}
                            />
                            <label htmlFor="load-json-file">
                                <Button
                                    variant="text"
                                    component="span"
                                    size="small"
                                    sx={{
                                        color: "text.secondary",
                                        textTransform: "none",
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    [TESTING] Load from JSON file
                                </Button>
                            </label>
                        </Box> */}
                    </Box>
                );
            case 0:
                return (
                    <Box sx={{ py: { xs: 0.5, sm: 1 }, pb: 8 }}>
                        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                            {mode === "load"
                                ? "Select an item to load its existing report:"
                                : "Select an item and report type:"}
                        </Typography>

                        {/* Show loaded item info if coming from JSON */}
                        {mode === "load" &&
                        (project || service || hse || quote) ? (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Loaded from API:
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight="medium"
                                    >
                                        {(() => {
                                            const selectedItem =
                                                project ||
                                                service ||
                                                hse ||
                                                quote;
                                            const number =
                                                selectedItem &&
                                                selectedItem.number
                                                    ? selectedItem.number
                                                    : "";
                                            const name =
                                                selectedItem &&
                                                selectedItem.name
                                                    ? selectedItem.name
                                                    : "";
                                            return `${number} - ${name}`;
                                        })()}
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setProject(null);
                                        setService(null);
                                        setHse(null);
                                        setQuote(null);
                                        setAvailableReports([]);
                                        setSelectedReportFromList(null);
                                        setLoadingReports(false);
                                        setLoadedReportId(null);
                                        setSelectionError(
                                            "Please select a Project, Service, HSE, or Quote",
                                        );
                                        // Clear report fields
                                        setReportType("");
                                        setMagnetType("");
                                        setFloorPlanData(null);
                                        setTestPoints([]);
                                        setFrequencies([]);
                                        setScores({});
                                        setWitnessName("");
                                        setWitnessCompany("");
                                        setNotes("");
                                        setWitness(true);
                                    }}
                                >
                                    Clear
                                </Button>
                            </Paper>
                        ) : (
                            <Stack direction="row" spacing={1}>
                                {menuSelection === 0 && (
                                    <ProjectPicker
                                        project={project}
                                        handleChangeProject={
                                            handleChangeProject
                                        }
                                        token={token}
                                    />
                                )}
                                {menuSelection === 1 && (
                                    <ServicePicker
                                        token={token}
                                        handleChangeProject={
                                            handleChangeService
                                        }
                                        errors={{ project: "" }}
                                    />
                                )}
                                {menuSelection === 2 && (
                                    <HSEPicker
                                        token={token}
                                        handleChangeProject={handleChangeHse}
                                        errors={{ project: "" }}
                                    />
                                )}
                                {menuSelection === 3 && (
                                    <QuotePicker
                                        token={token}
                                        handleChangeQuote={handleChangeQuote}
                                        errors={{ quote: "" }}
                                    />
                                )}
                                <ProjectTypeDropdown
                                    user={user}
                                    menuOptions={[
                                        "Projects",
                                        "Services",
                                        "HSE's",
                                        "Quotes",
                                    ]}
                                    menuSelection={menuSelection}
                                    setMenuSelection={setMenuSelection}
                                />
                            </Stack>
                        )}

                        {/* Show selection error */}
                        {selectionError && (
                            <Typography
                                color="error"
                                variant="body2"
                                sx={{ mt: 2 }}
                            >
                                {selectionError}
                            </Typography>
                        )}

                        {/* Show warning when no reports found in load mode */}
                        {mode === "load" &&
                            (project || service || hse || quote) &&
                            !loadingReports &&
                            availableReports.length === 0 && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        mt: 2,
                                        mb: 2,
                                        bgcolor: (theme) =>
                                            theme.palette.mode === "dark"
                                                ? "rgba(255, 167, 38, 0.15)"
                                                : "rgba(237, 108, 2, 0.08)",
                                        borderColor: "warning.main",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: "warning.dark",
                                            fontWeight: "medium",
                                        }}
                                        gutterBottom
                                    >
                                        ⚠️ No Reports Found
                                    </Typography>
                                    <Typography variant="body2">
                                        No existing RF Test Reports were found
                                        for this item. Please select a different
                                        Project, Service, HSE, or Quote, or
                                        switch to "Create New Report" mode.
                                    </Typography>
                                </Paper>
                            )}

                        {/* Show available reports list when in load mode with multiple reports */}
                        {mode === "load" && availableReports.length > 1 && (
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, mt: 2, mb: 2 }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    fontWeight="medium"
                                >
                                    Select a Report to Load:
                                </Typography>
                                <FormControl fullWidth>
                                    <InputLabel>Available Reports</InputLabel>
                                    <Select
                                        value={
                                            selectedReportFromList
                                                ? selectedReportFromList.id
                                                : ""
                                        }
                                        label="Available Reports"
                                        onChange={(e) => {
                                            const report = availableReports.find(
                                                (r) => r.id === e.target.value,
                                            );
                                            if (report) {
                                                setSelectedReportFromList(
                                                    report,
                                                );
                                                loadSpecificReport(report);
                                                if (handleOpenSnackbar) {
                                                    handleOpenSnackbar(
                                                        "success",
                                                        "Report loaded successfully!",
                                                    );
                                                }
                                            }
                                        }}
                                    >
                                        {availableReports.map((report) => (
                                            <MenuItem
                                                key={report.id}
                                                value={report.id}
                                            >
                                                {report.report_number ||
                                                    `Report #${report.id}`}{" "}
                                                - {report.test_type} (
                                                {new Date(
                                                    report.created_date,
                                                ).toLocaleDateString()}
                                                )
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Paper>
                        )}

                        {/* Show loaded report info with delete button */}
                        {mode === "load" &&
                            loadedReportId &&
                            (availableReports.length === 1 ||
                                (availableReports.length > 1 &&
                                    selectedReportFromList)) && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        mt: 2,
                                        mb: 2,
                                        bgcolor: "action.hover",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Currently Loaded Report:
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                        >
                                            {(() => {
                                                const report = availableReports.find(
                                                    (r) =>
                                                        r.id === loadedReportId,
                                                );
                                                if (report) {
                                                    return `${report.report_number ||
                                                        `Report #${report.id}`} - ${
                                                        report.test_type
                                                    }`;
                                                }
                                                return "Report loaded";
                                            })()}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        onClick={() =>
                                            setDeleteDialogOpen(true)
                                        }
                                    >
                                        Delete Report
                                    </Button>
                                </Paper>
                            )}

                        <DeleteReportDialog
                            open={deleteDialogOpen}
                            onClose={() => setDeleteDialogOpen(false)}
                            onConfirm={() => {
                                setDeleteDialogOpen(false);
                                handleDeleteReport();
                            }}
                            reportInfo={(() => {
                                const report = availableReports.find(
                                    (r) => r.id === loadedReportId,
                                );
                                return report || null;
                            })()}
                            user={user}
                        />

                        {/* Show fields when creating new report OR when an item is loaded AND a report is selected (if multiple) */}
                        {(mode === "create" ||
                            (mode === "load" &&
                                (project || service || hse || quote) &&
                                (availableReports.length === 1 ||
                                    (availableReports.length > 1 &&
                                        selectedReportFromList)))) && (
                            <>
                                <FormControl fullWidth sx={{ mt: 3 }}>
                                    <InputLabel>Report Type</InputLabel>
                                    <Select
                                        value={reportType}
                                        label="Report Type"
                                        onChange={(e) =>
                                            setReportType(e.target.value)
                                        }
                                    >
                                        <MenuItem value="B">Baseline</MenuItem>
                                        <MenuItem value="P">
                                            Preliminary
                                        </MenuItem>
                                        <MenuItem value="F">Final</MenuItem>
                                    </Select>
                                </FormControl>
                                <Box sx={{ mt: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={witness}
                                                onChange={(e) => {
                                                    setWitness(
                                                        e.target.checked,
                                                    );
                                                    if (!e.target.checked) {
                                                        setWitnessName("");
                                                        setWitnessCompany("");
                                                    }
                                                }}
                                            />
                                        }
                                        label="Include Witness"
                                    />
                                    {witness && (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label="Witness Name"
                                                value={witnessName}
                                                onChange={(e) =>
                                                    setWitnessName(
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label="Witness Company"
                                                value={witnessCompany}
                                                onChange={(e) =>
                                                    setWitnessCompany(
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                );
            case 1:
                return (
                    <Box sx={{ py: { xs: 0.5, sm: 1 }, pb: 8 }}>
                        <MagnetCarousel
                            value={magnetType}
                            onChange={handleChangeMagnetType}
                            darkState={darkState}
                        />
                    </Box>
                );
            case 2:
                return (
                    <Box sx={{ pb: { xs: 0, sm: 2 } }}>
                        <SimpleFloorPlan
                            onSave={handleSaveFloorPlan}
                            initialData={floorPlanData}
                            onChange={setFloorPlanData}
                        />
                    </Box>
                );
            case 3:
                return (
                    <Box sx={{ py: { xs: 0.5, sm: 1 }, pb: 8 }}>
                        <TestPointSelector
                            testPoints={testPoints}
                            onSave={handleSaveTestPoints}
                            onChange={setTestPoints}
                        />
                    </Box>
                );
            case 4:
                return (
                    <Box sx={{ py: { xs: 0.5, sm: 1 }, pb: 8 }}>
                        <FrequencyReferenceEntry
                            testPoints={testPoints}
                            frequencies={frequencies}
                            magnetType={magnetType}
                            onSave={handleSaveFrequencies}
                            onChange={setFrequencies}
                        />
                    </Box>
                );
            case 5:
                return (
                    <Box sx={{ py: { xs: 0.5, sm: 1 }, pb: 8 }}>
                        <ScoreEntry
                            testPoints={testPoints}
                            frequencies={frequencies}
                            scores={scores}
                            onSave={handleSaveScores}
                            onChange={setScores}
                            onComplete={setScoresComplete}
                        />
                    </Box>
                );
            case 6:
                return (
                    <Box sx={{ py: { xs: 0.5, sm: 1 }, pb: 8 }}>
                        <NotesEntry notes={notes} onChange={setNotes} />
                    </Box>
                );
            default:
                return "Unknown step";
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                px: { xs: 0, sm: 0.5, md: 1 },
                py: { xs: 0, sm: 0.5, md: 1 },
                pb: { xs: 10, sm: 10, md: 10 },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1, sm: 1.5 },
                    position: "relative",
                    border: 1,
                    borderColor: "primary.light",
                }}
            >
                {/* Start Over Button - Top Right */}
                {activeStep !== 7 && activeStep !== -1 && (
                    <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => setResetDialogOpen(true)}
                        sx={{
                            position: "absolute",
                            top: { xs: 8, sm: 12 },
                            right: { xs: 8, sm: 12 },
                            textTransform: "none",
                            zIndex: 10,
                        }}
                    >
                        Start Over
                    </Button>
                )}
                <ResetConfirmDialog
                    open={resetDialogOpen}
                    onClose={() => setResetDialogOpen(false)}
                    onConfirm={() => {
                        setResetDialogOpen(false);
                        handleReset();
                    }}
                />
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontSize: { xs: "1.5rem", sm: "2rem" },
                        textTransform: "uppercase",
                    }}
                >
                    RF Test Report
                </Typography>
                {(project || service || hse || quote) && activeStep >= 0 && (
                    <Box>
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            gutterBottom
                            sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                        >
                            {project &&
                                `Project: ${project.number} - ${project.name}`}
                            {service &&
                                `Service: ${service.number} - ${service.name}`}
                            {hse && `HSE: ${hse.number} - ${hse.name}`}
                            {quote && `Quote: ${quote.number} - ${quote.name}`}
                        </Typography>
                        {reportType && (
                            <Typography
                                variant="subtitle2"
                                color="primary"
                                sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                    fontWeight: 600,
                                }}
                            >
                                Report #:{" "}
                                {(project || service || hse || quote).number}-
                                {reportType}
                            </Typography>
                        )}
                    </Box>
                )}
                {activeStep === 7 ? (
                    <Box>
                        <Typography sx={{ mt: 2, mb: 1 }}>
                            All steps completed - RF Test Report{" "}
                            {isLoadedReport ? "updated" : "saved"}!
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                pt: 2,
                            }}
                        >
                            <Box sx={{ flex: "1 1 auto" }} />
                            <Button onClick={handleReset}>
                                Create New Report
                            </Button>
                        </Box>
                    </Box>
                ) : activeStep === -1 ? (
                    <Box sx={{ position: "relative" }}>
                        {getStepContent(activeStep)}
                    </Box>
                ) : (
                    <Box sx={{ position: "relative" }}>
                        {getStepContent(activeStep)}
                        <Box
                            sx={{
                                position: "fixed",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: "background.paper",
                                borderTop: 1,
                                borderColor: "divider",
                                px: 4,
                                py: { xs: 2, sm: 2 },
                                pb: { xs: 3, sm: 2 },
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                zIndex: 1200,
                                WebkitTransform: "translateZ(0)",
                                transform: "translateZ(0)",
                                WebkitBackfaceVisibility: "hidden",
                                backfaceVisibility: "hidden",
                            }}
                        >
                            <Button
                                onClick={handleBack}
                                disabled={activeStep === 0}
                                variant="outlined"
                            >
                                Back
                            </Button>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                            >
                                Step {activeStep + 1} of 7
                            </Typography>
                            {(activeStep === 0 ||
                                activeStep === 1 ||
                                activeStep === 2 ||
                                activeStep === 3 ||
                                activeStep === 4 ||
                                activeStep === 5 ||
                                activeStep === 6) && (
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        if (activeStep === 3) {
                                            handleSaveTestPoints(testPoints);
                                        } else if (activeStep === 4) {
                                            handleSaveFrequencies(frequencies);
                                        } else if (activeStep === 5) {
                                            handleSaveScores(scores);
                                        } else if (activeStep === 6) {
                                            handleSaveReport();
                                        } else {
                                            handleNext();
                                        }
                                    }}
                                    disabled={
                                        (activeStep === 0 &&
                                            (mode === "create"
                                                ? (() => {
                                                      const selections = [
                                                          project,
                                                          service,
                                                          hse,
                                                          quote,
                                                      ].filter(
                                                          (v) => v !== null,
                                                      );
                                                      return (
                                                          selections.length !==
                                                              1 ||
                                                          !reportType ||
                                                          (witness &&
                                                              (!witnessName ||
                                                                  !witnessCompany))
                                                      );
                                                  })()
                                                : (() => {
                                                      const selections = [
                                                          project,
                                                          service,
                                                          hse,
                                                          quote,
                                                      ].filter(
                                                          (v) => v !== null,
                                                      );
                                                      return (
                                                          selections.length !==
                                                              1 ||
                                                          availableReports.length ===
                                                              0 ||
                                                          (availableReports.length >
                                                              1 &&
                                                              !selectedReportFromList)
                                                      );
                                                  })())) ||
                                        (activeStep === 1 && !magnetType) ||
                                        (activeStep === 2 && !floorPlanData) ||
                                        (activeStep === 3 &&
                                            testPoints.length === 0) ||
                                        (activeStep === 4 &&
                                            !frequencies.some(
                                                (f) =>
                                                    f.frequency &&
                                                    f.reference !== "",
                                            )) ||
                                        (activeStep === 5 && !scoresComplete)
                                    }
                                    color={
                                        activeStep === 6 ? "success" : "primary"
                                    }
                                >
                                    {activeStep === 6
                                        ? isLoadedReport
                                            ? "Update"
                                            : "Save"
                                        : "Next"}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
